/**
 * Performance Monitoring System for Router Phase 4
 * 
 * Tracks timing metrics for:
 * - Complete route transitions
 * - Individual router guards
 * - Data loader execution
 * - Overall routing performance
 */

export interface PerformanceMetric {
  operation: string;
  startTime: number;
  endTime?: number;
  duration?: number;
  success: boolean;
  error?: string;
  metadata?: Record<string, any>;
}

export interface RouteTransitionMetrics {
  routeName: string;
  routePath: string;
  fromRoute?: string;
  guards: PerformanceMetric[];
  dataLoaders: PerformanceMetric[];
  totalDuration: number;
  timestamp: number;
  success: boolean;
}

export class PerformanceMonitor {
  private static instance: PerformanceMonitor;
  private metrics: Map<string, PerformanceMetric> = new Map();
  private routeTransitions: RouteTransitionMetrics[] = [];
  private currentTransition: Partial<RouteTransitionMetrics> | null = null;
  private isEnabled: boolean = false;
  // Keep a rolling log of completed operations for debugging/reporting
  private operationsLog: PerformanceMetric[] = [];

  static getInstance(): PerformanceMonitor {
    if (!PerformanceMonitor.instance) {
      PerformanceMonitor.instance = new PerformanceMonitor();
    }
    return PerformanceMonitor.instance;
  }

  /**
   * Enable performance monitoring (typically in development or when debugging)
   */
  enable(): void {
    this.isEnabled = true;
    console.log('🚀 [PerformanceMonitor] Performance monitoring enabled');
  }

  /**
   * Disable performance monitoring (for production)
   */
  disable(): void {
    this.isEnabled = false;
    console.log('🚀 [PerformanceMonitor] Performance monitoring disabled');
  }

  /**
   * Check if performance monitoring is enabled
   */
  get enabled(): boolean {
    return this.isEnabled;
  }

  /**
   * Start tracking a route transition
   */
  startRouteTransition(routeName: string, routePath: string, fromRoute?: string): string {
    if (!this.isEnabled) return '';

    const transitionId = `transition_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    
    this.currentTransition = {
      routeName,
      routePath,
      fromRoute,
      guards: [],
      dataLoaders: [],
      timestamp: Date.now(),
      success: false
    };

    console.log(`🚀 [PerformanceMonitor] Starting route transition: ${routePath} (${transitionId})`);
    
    // Also start timing the overall transition
    this.startTiming('route_transition', {
      routeName,
      routePath,
      fromRoute,
      transitionId
    });

    return transitionId;
  }

  /**
   * Complete a route transition with timing results
   */
  endRouteTransition(success: boolean = true): void {
    if (!this.isEnabled || !this.currentTransition) return;

    const transitionMetric = this.endTiming('route_transition', success);
    
    const completedTransition: RouteTransitionMetrics = {
      routeName: this.currentTransition.routeName!,
      routePath: this.currentTransition.routePath!,
      fromRoute: this.currentTransition.fromRoute,
      guards: this.currentTransition.guards!,
      dataLoaders: this.currentTransition.dataLoaders!,
      totalDuration: transitionMetric?.duration || 0,
      timestamp: this.currentTransition.timestamp!,
      success
    };

    this.routeTransitions.push(completedTransition);
    
    // Keep only last 50 transitions to prevent memory leaks
    if (this.routeTransitions.length > 50) {
      this.routeTransitions = this.routeTransitions.slice(-50);
    }

    console.log(`🚀 [PerformanceMonitor] Route transition completed: ${completedTransition.routePath} in ${completedTransition.totalDuration}ms`);
    this.logTransitionSummary(completedTransition);

    this.currentTransition = null;
  }

  /**
   * Start timing an operation
   */
  startTiming(operation: string, metadata?: Record<string, any>): string {
    if (!this.isEnabled) return '';

    const id = `${operation}_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    
    const metric: PerformanceMetric = {
      operation,
      startTime: performance.now(),
      success: false,
      metadata
    };

    this.metrics.set(id, metric);
    console.log(`⏱️ [PerformanceMonitor] Started timing: ${operation} (${id})`);

    return id;
  }

  /**
   * End timing an operation
   */
  endTiming(operationOrId: string, success: boolean = true, error?: string): PerformanceMetric | null {
    if (!this.isEnabled) return null;

    // Find metric by operation name or ID
    let metricId: string | null = null;
    let metric: PerformanceMetric | null = null;

    // First try as direct ID
    if (this.metrics.has(operationOrId)) {
      metricId = operationOrId;
      metric = this.metrics.get(operationOrId)!;
    } else {
      // Search by operation name (find the most recent one)
      for (const [id, m] of this.metrics.entries()) {
        if (m.operation === operationOrId && !m.endTime) {
          metricId = id;
          metric = m;
          break;
        }
      }
    }

    if (!metric || !metricId) {
      console.warn(`⚠️ [PerformanceMonitor] No active timing found for: ${operationOrId}`);
      return null;
    }

    const endTime = performance.now();
    const duration = endTime - metric.startTime;

    metric.endTime = endTime;
    metric.duration = duration;
    metric.success = success;
    if (error) metric.error = error;

    console.log(`⏱️ [PerformanceMonitor] Completed timing: ${metric.operation} in ${duration.toFixed(2)}ms`);

    // Add to current transition if applicable
    if (this.currentTransition) {
      if (metric.operation.includes('guard')) {
        this.currentTransition.guards!.push({ ...metric });
      } else if (metric.operation.includes('loader') || metric.operation.includes('DataLoader')) {
        this.currentTransition.dataLoaders!.push({ ...metric });
      }
    }

    // Add to global operations log
    this.operationsLog.push({ ...metric });
    if (this.operationsLog.length > 500) {
      this.operationsLog = this.operationsLog.slice(-500);
    }

    return metric;
  }

  /**
   * Time an async operation
   */
  async timeAsyncOperation<T>(
    operation: string,
    asyncOperation: () => Promise<T>,
    metadata?: Record<string, any>
  ): Promise<T> {
    if (!this.isEnabled) {
      return asyncOperation();
    }

    const timingId = this.startTiming(operation, metadata);
    
    try {
      const result = await asyncOperation();
      this.endTiming(timingId, true);
      return result;
    } catch (error) {
      this.endTiming(timingId, false, error instanceof Error ? error.message : 'Unknown error');
      throw error;
    }
  }

  /**
   * Get performance summary for recent transitions
   */
  getPerformanceSummary(): {
    recentTransitions: RouteTransitionMetrics[];
    averageTransitionTime: number;
    averageGuardTime: number;
    averageDataLoaderTime: number;
    slowestRoutes: Array<{ route: string; avgTime: number; count: number }>;
  } {
    const recent = this.routeTransitions.slice(-20);
    
    const avgTransitionTime = recent.length > 0 
      ? recent.reduce((sum, t) => sum + t.totalDuration, 0) / recent.length 
      : 0;

    const allGuards = recent.flatMap(t => t.guards);
    const avgGuardTime = allGuards.length > 0
      ? allGuards.reduce((sum, g) => sum + (g.duration || 0), 0) / allGuards.length
      : 0;

    const allLoaders = recent.flatMap(t => t.dataLoaders);
    const avgDataLoaderTime = allLoaders.length > 0
      ? allLoaders.reduce((sum, l) => sum + (l.duration || 0), 0) / allLoaders.length
      : 0;

    // Group by route and calculate averages
    const routeGroups = new Map<string, { totalTime: number; count: number }>();
    recent.forEach(t => {
      const existing = routeGroups.get(t.routePath) || { totalTime: 0, count: 0 };
      existing.totalTime += t.totalDuration;
      existing.count += 1;
      routeGroups.set(t.routePath, existing);
    });

    const slowestRoutes = Array.from(routeGroups.entries())
      .map(([route, data]) => ({
        route,
        avgTime: data.totalTime / data.count,
        count: data.count
      }))
      .sort((a, b) => b.avgTime - a.avgTime)
      .slice(0, 10);

    return {
      recentTransitions: recent,
      averageTransitionTime: avgTransitionTime,
      averageGuardTime: avgGuardTime,
      averageDataLoaderTime: avgDataLoaderTime,
      slowestRoutes
    };
  }

  /**
   * Get recent completed operations (guards, loaders, functions, fetches, etc.)
   */
  getRecentOperations(max: number = 200): PerformanceMetric[] {
    return this.operationsLog.slice(-max);
  }

  /**
   * Get recorded route transitions (bounded list)
   */
  getRouteTransitions(): RouteTransitionMetrics[] {
    return [...this.routeTransitions];
  }

  /**
   * Log a detailed summary of a transition
   */
  private logTransitionSummary(transition: RouteTransitionMetrics): void {
    console.group(`📊 Route Transition Summary: ${transition.routePath}`);
    console.log(`Total Duration: ${transition.totalDuration.toFixed(2)}ms`);
    console.log(`Guards (${transition.guards.length}):`, transition.guards.map(g => `${g.operation}: ${g.duration?.toFixed(2)}ms`));
    console.log(`Data Loaders (${transition.dataLoaders.length}):`, transition.dataLoaders.map(l => `${l.operation}: ${l.duration?.toFixed(2)}ms`));
    console.log(`Success: ${transition.success}`);
    console.groupEnd();
  }

  /**
   * Clear all performance data
   */
  clear(): void {
    this.metrics.clear();
    this.routeTransitions = [];
    this.currentTransition = null;
    console.log('🧹 [PerformanceMonitor] Performance data cleared');
  }

  /**
   * Export performance data for analysis
   */
  exportData(): {
    transitions: RouteTransitionMetrics[];
    summary: ReturnType<PerformanceMonitor['getPerformanceSummary']>;
    operations: PerformanceMetric[];
  } {
    return {
      transitions: [...this.routeTransitions],
      summary: this.getPerformanceSummary(),
      operations: this.getRecentOperations(500)
    };
  }
}

// Singleton instance
export const performanceMonitor = PerformanceMonitor.getInstance();

// Global helpers for browser console debugging
if (typeof window !== 'undefined') {
  (window as any).performanceMonitor = {
    get enabled() { return performanceMonitor.enabled; },
    getSummary: () => performanceMonitor.getPerformanceSummary(),
    exportData: () => performanceMonitor.exportData(),
    clear: () => performanceMonitor.clear(),
    enable: () => performanceMonitor.enable(),
    disable: () => performanceMonitor.disable(),
    startTiming: (op: string, meta?: any) => performanceMonitor.startTiming(op, meta),
    endTiming: (id: string, success?: boolean) => performanceMonitor.endTiming(id, success),
    instance: performanceMonitor
  };
} 